import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './App.css';

const BASE_PRICE = 1495;

const CONFIG_OPTIONS = {
    safety_rating: [
        { code: 'S0', label: 'Standard Edition', price: 0 },
        { code: 'S1', label: 'Advanced Shielded (AS-v1)', price: 285 },
        { code: 'S2', label: 'C-Grade Extreme (XP-X)', price: 640 }
    ],
    contact_media: [
        { code: 'M1', label: 'Armor-Core-SS', price: 0 },
        { code: 'M2', label: 'Titan-Alloy-C', price: 195 }
    ],
    probe_config: [
        { code: 'SR', label: 'Single Probe', price: 0 },
        { code: 'SC', label: 'Cable', price: 125 },
        { code: 'DR', label: 'Double Probe', price: 340 },
        { code: 'DC', label: 'Double Cable', price: 465 }
    ],
    thermal_profile: [
        { code: 'TP1', label: 'Precision Range', price: 0 },
        { code: 'TP2', label: 'High Heat', price: 175 },
        { code: 'TP3', label: 'Standard Environmental', price: 0 }
    ],
    mounting_interface: [
        { code: 'MI-A', label: 'Threaded Port 3/4', price: 0 },
        { code: 'MI-B', label: 'Threaded Port 1', price: 35 },
        { code: 'MI-C', label: 'Threaded Port 1.5', price: 55 },
        { code: 'MI-D', label: 'Flange Mount 150', price: 185 },
        { code: 'MI-E', label: 'Flange Mount 300', price: 285 }
    ],
    signal_protocol: [
        { code: 'P1', label: '2-Wire Sync', price: 0 },
        { code: 'P2', label: 'Dual-Channel Sync', price: 145 },
        { code: 'P3', label: 'Relay-Bridge v2', price: 225 }
    ],
    installation_path: [
        { code: 'H', label: 'Horizontal', price: 95 },
        { code: 'V', label: 'Vertical', price: 0 }
    ],
    enhancements: [
        { code: 'GPC', label: 'Gas Compensation', price: 185 },
        { code: 'CAL', label: 'Factory Cert', price: 95 },
        { code: 'PRO', label: 'Weather Shell', price: 75 },
        { code: 'TAG', label: 'Metal Ident Tag', price: 25 }
    ]
};

const EngineeringDrawingViewer = () => {
    const [svgContent, setSvgContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const [config, setConfig] = useState({
        safety_rating: 'S0',
        contact_media: 'M1',
        probe_config: '',
        thermal_profile: 'TP1',
        mounting_interface: 'MI-A',
        active_length: '10',
        active_length_2: '10',
        signal_protocol: 'P1',
        installation_path: 'V',
        selected_enhancements: []
    });

    const [flashing, setFlashing] = useState(false);

    useEffect(() => {
        const fetchDrawing = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams({
                    customer: 'Midwest Distribution',
                    project: 'WaveMaster Series Integration',
                    title: 'Parametric Engineering Drawing',
                    model_number: generateModelNumber(),
                    date: new Date().toLocaleDateString(),
                    rev: '1',
                    sheet: '1 of 1',
                    probe_config: config.probe_config,
                    installation_path: config.installation_path,
                    active_length: config.active_length,
                    active_length_2: config.active_length_2
                });

                const response = await fetch(`https://industriquote.com/api/generate-2d-drawing?${params.toString()}`);
                if (!response.ok) {
                    throw new Error('Failed to fetch drawing');
                }
                const svgText = await response.text();
                setSvgContent(svgText);
            } catch (err) {
                console.error('Error fetching drawing:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        const debounceTimer = setTimeout(() => {
            fetchDrawing();
        }, 500);

        return () => clearTimeout(debounceTimer);
    }, [config]);

    // Flash effect when config changes
    useEffect(() => {
        setFlashing(true);
        const timer = setTimeout(() => setFlashing(false), 300);
        return () => clearTimeout(timer);
    }, [config]);

    const handleOptionChange = (key, value) => {
        setConfig(prev => {
            const newConfig = { ...prev, [key]: value };
            if (key === 'probe_config') {
                const isOrientable = ['SR', 'HP'].includes(value);
                if (!isOrientable) {
                    newConfig.installation_path = 'V';
                }
            }
            return newConfig;
        });
    };

    const toggleSecondaryEnhancement = (code) => {
        setConfig(prev => {
            const opts = [...prev.selected_enhancements];
            const index = opts.indexOf(code);
            if (index === -1) opts.push(code);
            else opts.splice(index, 1);
            return { ...prev, selected_enhancements: opts };
        });
    };

    const generateModelNumber = () => {
        const { safety_rating, contact_media, probe_config, thermal_profile, mounting_interface, active_length, active_length_2, signal_protocol, installation_path, selected_enhancements } = config;
        const isDouble = ['DR', 'DC'].includes(probe_config);
        const length1 = active_length || '0';
        const length2 = active_length_2 || '0';

        const segments = [
            'WM8K',
            probe_config || '____',
            installation_path,
            isDouble ? `${length1}ft-${length2}ft` : `${length1}ft`,
            safety_rating,
            contact_media,
            thermal_profile,
            mounting_interface,
            signal_protocol
        ];
        let base = segments.join('-');
        if (selected_enhancements.length > 0) {
            base += '-' + selected_enhancements.join('-');
        }
        return base;
    };

    const calculateTotal = () => {
        const priceFor = (group, code) => {
            const opt = CONFIG_OPTIONS[group].find(o => o.code === code);
            return opt ? opt.price : 0;
        };
        let total = BASE_PRICE;
        total += priceFor('safety_rating', config.safety_rating);
        total += priceFor('contact_media', config.contact_media);
        total += priceFor('probe_config', config.probe_config);
        total += priceFor('thermal_profile', config.thermal_profile);
        total += priceFor('mounting_interface', config.mounting_interface);
        total += priceFor('signal_protocol', config.signal_protocol);
        total += priceFor('installation_path', config.installation_path);
        config.selected_enhancements.forEach(code => {
            total += priceFor('enhancements', code);
        });
        const lengthPrice = (ft) => Math.ceil(Math.max(parseFloat(ft) || 0, 0) / 3) * 25;
        total += lengthPrice(config.active_length);
        if (['DR', 'DC'].includes(config.probe_config)) {
            total += lengthPrice(config.active_length_2);
        }
        return total;
    };

    const modelNumber = generateModelNumber();
    const calculatedTotal = calculateTotal();
    const isOrientationEnabled = ['SR', 'HP'].includes(config.probe_config);

    return (
        <div className="App">
            <style>
                {`
                @keyframes flash-glow {
                    0% { background: #fef0e0; transform: scale(1); }
                    50% { background: #fde0b8; transform: scale(1.01); }
                    100% { background: #fef0e0; transform: scale(1); }
                }
                .model-number-box.flash {
                    animation: flash-glow 0.3s ease-out;
                    border-color: #f47920 !important;
                    box-shadow: 0 0 10px rgba(29, 78, 216, 0.15);
                }
                `}
            </style>
            <div className="sidebar">
                <button
                    className="control-btn"
                    style={{ width: '100%', padding: '0 15px', marginBottom: '1.5rem', fontSize: '0.75rem', background: '#6b7280', fontFamily: 'inherit' }}
                    onClick={() => navigate('/')}
                >
                    Back to Hub
                </button>

                <div style={{
                    background: '#ffffff',
                    paddingBottom: '1.5rem',
                    borderBottom: '1px solid #e5e7eb',
                    marginBottom: '1.5rem',
                    position: 'sticky',
                    top: 'var(--sidebar-sticky-top)',
                    zIndex: 20
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.35rem', paddingTop: '1.5rem' }}>
                        <div style={{ flexShrink: 0, width: '18px', height: '18px', background: '#f47920', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: '900', fontSize: '0.65rem' }}>IQ</div>
                        <span style={{ fontSize: '0.62rem', fontWeight: '700', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#6b7280', flex: '1', minWidth: '200px' }}>IndustriQuote · WaveMaster Series</span>
                    </div>
                    <h1 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#111827', marginBottom: '0.75rem', letterSpacing: '-0.01em' }}>2D Engineering Page</h1>

                    <div
                        className={`model-number-box ${flashing ? 'flash' : ''}`}
                        style={{
                            background: '#fef0e0',
                            border: '1px solid #f9c58d',
                            padding: '0.85rem 1rem',
                            borderRadius: '2px',
                            fontSize: '0.8rem',
                            fontFamily: 'JetBrains Mono, monospace',
                            color: '#7c3d0a',
                            fontWeight: '600',
                            wordBreak: 'break-all',
                            boxSizing: 'border-box',
                            transition: 'all 0.3s ease'
                        }}
                    >
                        {modelNumber}
                    </div>

                    <div style={{
                        marginTop: '0.75rem',
                        padding: '0.65rem 0.75rem',
                        background: '#dbe6f5',
                        border: '1px solid #a8c0dd',
                        borderRadius: '2px',
                        boxSizing: 'border-box'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                            <span style={{ fontSize: '0.72rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#1a2e4a' }}>Your Organization's Price</span>
                            <span style={{ fontSize: '1.2rem', fontWeight: '800', color: '#1a2e4a', fontVariantNumeric: 'tabular-nums' }}>
                                ${(calculatedTotal * 0.75).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.3rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                            <span style={{ fontSize: '0.68rem', color: '#6b7280', fontWeight: '500' }}>List price (–25%)</span>
                            <span style={{ fontSize: '0.82rem', color: '#9ca3af', fontVariantNumeric: 'tabular-nums', textDecoration: 'line-through' }}>
                                ${calculatedTotal.toLocaleString()}
                            </span>
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    {/* 1. Probe Config (Interactive) */}
                    <ConfigSection 
                        label="Probe Config" 
                        value={config.probe_config} 
                        options={CONFIG_OPTIONS.probe_config} 
                        onChange={(val) => handleOptionChange('probe_config', val)}
                        placeholder="Select Probe Configuration..."
                    />

                    {!config.probe_config ? (
                        <div style={{
                            background: '#f8fafc',
                            border: '1px dashed #cbd5e1',
                            borderRadius: '2px',
                            padding: '2.5rem 1.5rem',
                            textAlign: 'center',
                            marginTop: '1rem'
                        }}>
                            <div style={{ 
                                width: '32px', 
                                height: '32px', 
                                background: '#e2e8f0', 
                                borderRadius: '50%', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                margin: '0 auto 1rem',
                                color: '#64748b',
                                fontSize: '1rem',
                                fontWeight: '800'
                            }}>!</div>
                            <div style={{ fontSize: '0.65rem', color: '#1a2e4a', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: '800', marginBottom: '0.5rem' }}>
                                Selection Required
                            </div>
                            <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0, lineHeight: '1.5', fontWeight: '500' }}>
                                Please select a probe configuration to unlock model specifications and available options.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* 2. Installation Path (Interactive) */}
                            <div style={{ opacity: isOrientationEnabled ? 1 : 0.4, pointerEvents: isOrientationEnabled ? 'auto' : 'none' }}>
                                <ConfigSection 
                                    label="Installation Path" 
                                    value={config.installation_path} 
                                    options={CONFIG_OPTIONS.installation_path} 
                                    onChange={(val) => handleOptionChange('installation_path', val)} 
                                    disabled={!isOrientationEnabled}
                                />
                            </div>

                            {/* 3. Active Length (Interactive) */}
                            <div className="config-section">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.25rem', marginBottom: '0.5rem' }}>
                                    <div style={{ fontSize: '0.65rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: '700' }}>
                                        {['DR', 'DC'].includes(config.probe_config) ? "Active Lengths (ft)" : "Active Length (ft)"}
                                    </div>
                                    <div style={{ fontSize: '0.65rem', color: '#d97706', fontWeight: '700', letterSpacing: '0.04em' }}>
                                        $25 / 3 ft
                                    </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type="text"
                                            value={config.active_length}
                                            onChange={(e) => handleOptionChange('active_length', e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1').slice(0, 6))}
                                            placeholder="Primary"
                                            style={{
                                                width: '100%',
                                                padding: '0.7rem 0.8rem',
                                                background: '#f9fafb',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '2px',
                                                color: '#111827',
                                                fontSize: '0.88rem',
                                                outline: 'none',
                                                fontFamily: 'inherit',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                        {['DR', 'DC'].includes(config.probe_config) ? (
                                            <span style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.65rem', color: '#9ca3af', fontWeight: '600', letterSpacing: '0.06em' }}>PROBE 1 · ft</span>
                                        ) : (
                                            <span style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.65rem', color: '#9ca3af', fontWeight: '600', letterSpacing: '0.06em' }}>ft</span>
                                        )}
                                    </div>

                                    {['DR', 'DC'].includes(config.probe_config) && (
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                type="text"
                                                value={config.active_length_2}
                                                onChange={(e) => handleOptionChange('active_length_2', e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1').slice(0, 6))}
                                                placeholder="Secondary"
                                                style={{
                                                    width: '100%',
                                                    padding: '0.7rem 0.8rem',
                                                    background: '#f9fafb',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '2px',
                                                    color: '#111827',
                                                    fontSize: '0.88rem',
                                                    outline: 'none',
                                                    fontFamily: 'inherit',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                            <span style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.65rem', color: '#9ca3af', fontWeight: '600', letterSpacing: '0.06em' }}>PROBE 2 · ft</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1.5rem', marginTop: '0.5rem' }}>
                                <h3 style={{ fontSize: '0.62rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '1rem', fontWeight: '700' }}>Product Specifications</h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                    <ConfigSection label="Safety Rating" value={config.safety_rating} options={CONFIG_OPTIONS.safety_rating} onChange={(val) => handleOptionChange('safety_rating', val)} />
                                    <ConfigSection label="Contact Media" value={config.contact_media} options={CONFIG_OPTIONS.contact_media} onChange={(val) => handleOptionChange('contact_media', val)} />
                                    <ConfigSection label="Thermal Profile" value={config.thermal_profile} options={CONFIG_OPTIONS.thermal_profile} onChange={(val) => handleOptionChange('thermal_profile', val)} />
                                    <ConfigSection label="Mounting Interface" value={config.mounting_interface} options={CONFIG_OPTIONS.mounting_interface} onChange={(val) => handleOptionChange('mounting_interface', val)} />
                                    <ConfigSection label="Signal Protocol" value={config.signal_protocol} options={CONFIG_OPTIONS.signal_protocol} onChange={(val) => handleOptionChange('signal_protocol', val)} />

                                    <div className="config-section" style={{ marginBottom: '2rem' }}>
                                        <div className="section-title" style={{ fontSize: '0.65rem', color: '#6b7280', marginBottom: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: '700', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.25rem' }}>Enhancements</div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                                            {CONFIG_OPTIONS.enhancements.map(opt => {
                                                const isSelected = config.selected_enhancements.includes(opt.code);
                                                return (
                                                    <button
                                                        key={opt.code}
                                                        onClick={() => toggleSecondaryEnhancement(opt.code)}
                                                        style={{
                                                            padding: '0.6rem',
                                                            fontSize: '0.72rem',
                                                            borderRadius: '2px',
                                                            border: `1px solid ${isSelected ? '#1a2e4a' : '#d1d5db'}`,
                                                            background: isSelected ? '#dbe6f5' : '#f9fafb',
                                                            color: isSelected ? '#1a2e4a' : '#6b7280',
                                                            textAlign: 'center',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.15s ease',
                                                            fontWeight: isSelected ? '700' : '500',
                                                            fontFamily: 'inherit'
                                                        }}
                                                    >
                                                        <div>{opt.code}</div>
                                                        <div style={{ fontSize: '0.65rem', marginTop: '2px', color: isSelected ? '#f47920' : '#9ca3af', fontWeight: '600' }}>
                                                            +${opt.price.toLocaleString()}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1.5rem', marginTop: '1rem', marginBottom: '4rem' }}>
                    <div className="section-title">Drawing Options</div>
                    <div className="derived-outputs">
                        <div className="derived-row">
                            <span>Format:</span>
                            <strong>ISO A3</strong>
                        </div>
                        <div className="derived-row">
                            <span>Size:</span>
                            <strong>420 x 297 mm</strong>
                        </div>
                        <div className="derived-row">
                            <span>Scale:</span>
                            <strong>1:1</strong>
                        </div>
                    </div>


                </div>
            </div>

            <div className="canvas-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem', overflow: 'auto' }}>
                {loading && (
                    <div className="loading-overlay">
                        Generating Drawing Template...
                    </div>
                )}
                {error && (
                    <div style={{ color: 'var(--danger)', fontWeight: '700' }}>
                        Error: {error}
                    </div>
                )}
                {!loading && !error && (
                    <div
                        className="drawing-svg-container"
                        dangerouslySetInnerHTML={{ __html: svgContent }}
                    />
                )}
            </div>
        </div>
    );
};

// Helper component for select sections
const ConfigSection = ({ label, value, options, onChange, disabled, placeholder }) => {
    const currentOpt = options.find(o => o.code === value);

    return (
        <div className="config-section" style={{ opacity: disabled ? 0.6 : 1 }}>
            <div className="section-title" style={{ fontSize: '0.65rem', color: '#6b7280', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: '700', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.25rem' }}>
                {label}
            </div>

            {disabled ? (
                <div style={{
                    width: '100%',
                    padding: '0.7rem 0.8rem',
                    background: '#f3f4f6',
                    border: '1px solid #e5e7eb',
                    borderRadius: '2px',
                    color: '#9ca3af',
                    fontSize: '0.88rem',
                    fontStyle: 'italic',
                    boxSizing: 'border-box'
                }}>
                    {currentOpt ? `${currentOpt.code} - ${currentOpt.label}` : value}
                </div>
            ) : (
                <select
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    style={{
                        width: '100%',
                        padding: '0.7rem 0.8rem',
                        background: '#f9fafb',
                        border: '1px solid #d1d5db',
                        borderRadius: '2px',
                        color: value ? '#111827' : '#9ca3af',
                        fontSize: '0.88rem',
                        outline: 'none',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box'
                    }}
                >
                    {placeholder && <option value="" disabled>{placeholder}</option>}
                    {options.map(opt => (
                        <option key={opt.code} value={opt.code}>
                            {opt.code} — {opt.label}{opt.price > 0 ? ` (+$${opt.price.toLocaleString()})` : ''}
                        </option>
                    ))}
                </select>
            )}
        </div>
    );
};

export default EngineeringDrawingViewer;
